import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Loader2, Sunrise, Target, Droplets, Flame, ArrowRight,
  X, CheckCircle, AlertTriangle, Lightbulb, BarChart3, Activity, Sparkles, RefreshCw,
} from 'lucide-react';
import { getLocalDateString } from '../utils/dateUtils';
import { PillarDots } from './PillarDots';

// Per-day dismiss. Re-appears tomorrow automatically.
const DISMISS_KEY = 'yumix_morning_debrief_dismissed';
// Per-day cache so navigating back to the Dashboard never re-triggers a full LLM fetch.
const CACHE_PREFIX = 'yumix_morning_debrief_cache';
const getCacheKey = (userId) => `${CACHE_PREFIX}_${userId}_${getLocalDateString()}`;
const purgeOldCacheEntries = () => {
  try {
    const today = getLocalDateString();
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith(`${CACHE_PREFIX}_`) && !k.endsWith(today)) localStorage.removeItem(k);
    });
  } catch { /* */ }
};

const MorningDebrief = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (fr, en) => (language === 'fr' ? fr : en);
  const fr = language === 'fr';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    if (!user) return;
    const today = getLocalDateString();
    const lastDismissed = localStorage.getItem(DISMISS_KEY);
    if (lastDismissed === today) {
      setDismissed(true);
      return;
    }
    const hour = new Date().getHours();
    setActiveTab(hour < 12 ? 'yesterday' : 'today');

    // 1) Try the per-day cache first — instant render, zero network.
    purgeOldCacheEntries();
    try {
      const cached = localStorage.getItem(getCacheKey(user.user_id));
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.data) {
          setData(parsed.data);
          return; // Done — user can hit the 🔄 to refresh manually.
        }
      }
    } catch { /* fall through to network */ }

    // 2) No cache → fetch once and persist.
    setLoading(true);
    api.get('/debrief/morning')
      .then(res => {
        const d = res.data.morning_debrief;
        setData(d);
        try {
          localStorage.setItem(getCacheKey(user.user_id), JSON.stringify({ data: d, ts: Date.now() }));
        } catch { /* quota — ignore */ }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Manual refresh: clears the per-day cache and forces a fresh LLM analysis.
  // Triggered by the 🔄 icon on the compact card AND inside the modal header.
  const refresh = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!user) return;
    setLoading(true);
    try {
      try { localStorage.removeItem(getCacheKey(user.user_id)); } catch {}
      const res = await api.get('/debrief/morning');
      const d = res.data.morning_debrief;
      setData(d);
      try {
        localStorage.setItem(getCacheKey(user.user_id), JSON.stringify({ data: d, ts: Date.now() }));
      } catch {}
    } catch {
      /* keep existing data on error */
    } finally {
      setLoading(false);
    }
  };

  const dismiss = (e) => {
    if (e) e.stopPropagation();
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, getLocalDateString()); } catch {}
  };

  if (!user || dismissed) return null;

  // ── COMPACT CARD on the Dashboard ──
  // Loading (spinner)
  if (loading && !data) {
    return (
      <div
        className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-card to-orange-500/5 px-3 py-2.5 flex items-center gap-2.5"
        data-testid="morning-debrief-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin text-amber-400 shrink-0" />
        <span className="text-xs text-amber-300 font-medium">
          {t('YUMIX analyse ta journée...', 'YUMIX is analyzing your day...')}
        </span>
      </div>
    );
  }

  if (!data) return null;

  const { alert_mode } = data;
  const accent = alert_mode ? 'red' : 'amber';

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className={`w-full group flex items-center gap-3 rounded-xl border transition-all active:scale-[0.99] px-3 py-2.5 text-left ${
          accent === 'red'
            ? 'border-red-500/40 bg-gradient-to-r from-red-500/10 via-orange-500/5 to-transparent hover:from-red-500/15'
            : 'border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-orange-500/5 to-transparent hover:from-amber-500/20'
        }`}
        data-testid="morning-debrief-compact"
      >
        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
          accent === 'red' ? 'bg-red-500/20' : 'bg-amber-500/20'
        }`}>
          <Sparkles className={`w-5 h-5 ${accent === 'red' ? 'text-red-300' : 'text-amber-300'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-widest font-bold ${
            accent === 'red' ? 'text-red-300' : 'text-amber-300'
          }`}>
            {alert_mode
              ? t('Briefing · Alerte active', 'Briefing · Alert active')
              : t('Briefing du jour', 'Daily Briefing')}
          </p>
          <p className="text-xs font-semibold text-foreground leading-tight" data-testid="morning-debrief-cta-label">
            {t('Ton Débriefing est prêt — appuie pour lire', 'Your Briefing is ready — tap to read')}
          </p>
        </div>
        <ArrowRight className={`w-4 h-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${
          accent === 'red' ? 'text-red-300/70' : 'text-amber-300/70'
        }`} />
        <span
          role="button"
          tabIndex={0}
          onClick={refresh}
          onKeyDown={(e) => { if (e.key === 'Enter') refresh(e); }}
          className="shrink-0 text-muted-foreground/60 hover:text-foreground p-1"
          data-testid="morning-debrief-refresh"
          aria-label={t('Actualiser', 'Refresh')}
          title={t('Actualiser le briefing', 'Refresh briefing')}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-amber-300' : ''}`} />
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={dismiss}
          onKeyDown={(e) => { if (e.key === 'Enter') dismiss(e); }}
          className="shrink-0 text-muted-foreground/60 hover:text-foreground p-1"
          data-testid="morning-debrief-dismiss"
          aria-label={t('Fermer', 'Dismiss')}
        >
          <X className="w-3.5 h-3.5" />
        </span>
      </button>

      {/* Bottom-sheet modal with full content — rendered via Portal to document.body
          to ESCAPE any ancestor stacking context (Aurora glow, transforms, filters)
          that would otherwise push us behind the bottom navigation. */}
      {modalOpen && createPortal(
        <MorningDebriefModal
          data={data}
          language={language}
          loading={loading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          refresh={refresh}
          navigate={navigate}
          onClose={() => setModalOpen(false)}
          t={t}
          fr={fr}
        />,
        document.body,
      )}
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Full-screen / bottom-sheet modal — the actual long-form briefing content.
// ════════════════════════════════════════════════════════════════════════
const MorningDebriefModal = ({ data, language, loading, activeTab, setActiveTab, refresh, navigate, onClose, t, fr }) => {
  const { greeting, suggestions, yesterday, today, gaps, clinical_mode, alert_mode, active_alert } = data;
  const hasToday = !!today;
  const stats = (activeTab === 'today' && hasToday) ? today : yesterday;
  const hasGaps = gaps && gaps.length > 0;

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="morning-debrief-modal"
      style={{
        zIndex: 2147483000,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        pointerEvents: 'auto',
        touchAction: 'none',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-t border-x border-border/60 sm:border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col shadow-2xl yumix-sheet-enter"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Drag handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header (sticky) */}
        <div className="flex items-center justify-between px-5 pt-3 pb-3 border-b border-border/40">
          <div className="flex items-center gap-2 flex-wrap">
            <Sunrise className="w-4 h-4 text-amber-400" />
            <span className="font-heading text-xs uppercase tracking-widest text-amber-400">
              {t('Briefing du jour', 'Daily Briefing')}
            </span>
            {clinical_mode && <span className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded-full uppercase tracking-wider">clinique</span>}
            {alert_mode && (
              <span className="text-[9px] px-1.5 py-0.5 bg-red-500/30 text-red-200 rounded-full uppercase tracking-wider animate-pulse">
                alerte
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={refresh}
              disabled={loading}
              className="text-muted-foreground hover:text-foreground p-1.5 disabled:opacity-40"
              data-testid="morning-debrief-modal-refresh"
              aria-label={t('Actualiser', 'Refresh')}
              title={t('Forcer une nouvelle analyse', 'Force a fresh analysis')}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-amber-300' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1.5"
              data-testid="morning-debrief-modal-close"
              aria-label={t('Fermer', 'Close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {/* Tabs */}
          {hasToday && (
            <div className="flex gap-1 mb-3 bg-secondary/40 rounded-lg p-0.5" data-testid="debrief-tabs">
              <button
                onClick={() => { setActiveTab('today'); if (!loading) refresh(); }}
                className={`flex-1 text-[11px] uppercase tracking-wider font-semibold py-1.5 rounded-md transition-all ${
                  activeTab === 'today' ? 'bg-amber-500/20 text-amber-300 shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-today"
              >
                <Activity className="w-3 h-3 inline mr-1" />
                {t("Aujourd'hui", 'Today')}
              </button>
              <button
                onClick={() => setActiveTab('yesterday')}
                className={`flex-1 text-[11px] uppercase tracking-wider font-semibold py-1.5 rounded-md transition-all ${
                  activeTab === 'yesterday' ? 'bg-amber-500/20 text-amber-300 shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-yesterday"
              >
                <Sunrise className="w-3 h-3 inline mr-1" />
                {t('Hier', 'Yesterday')}
              </button>
            </div>
          )}

          {/* Greeting (yesterday only) */}
          {activeTab === 'yesterday' && greeting && (
            <p className="text-sm text-foreground/90 leading-relaxed mb-4" data-testid="morning-greeting">{greeting}</p>
          )}

          {/* Live banner */}
          {activeTab === 'today' && (
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              {t(
                `Progression en direct · ${stats?.meals_logged || 0} repas loggés aujourd'hui. Pousse les chiffres avant la fin de la journée.`,
                `Live progress · ${stats?.meals_logged || 0} meals logged today. Push the numbers before day-end.`,
              )}
            </p>
          )}

          {/* Iter. 406.fr.40 — 6-dot pillar mini-gauge (only for TODAY) */}
          {activeTab === 'today' && today?.pillars_snapshot && (
            <PillarDots pillars={today.pillars_snapshot} language={language} t={t} source="morning-debrief" />
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatPill icon={<Target className="w-3.5 h-3.5" />} label={t('Protéines', 'Protein')}
              value={`${stats?.protein_g || 0}g`} pct={stats?.protein_pct || 0}
              color={stats?.protein_pct >= 80 ? 'emerald' : stats?.protein_pct >= 50 ? 'orange' : 'red'} />
            <StatPill icon={<Flame className="w-3.5 h-3.5" />} label="Calories"
              value={`${stats?.calories || 0}`} pct={stats?.calories_pct || 0}
              color={stats?.calories_pct >= 80 && stats?.calories_pct <= 115 ? 'emerald' : 'orange'} />
            <StatPill icon={<Droplets className="w-3.5 h-3.5" />} label={t('Eau', 'Water')}
              value={`${stats?.water_glasses || 0}/${stats?.water_target || 10}`}
              pct={stats?.water_target ? Math.min(100, Math.round((stats.water_glasses / stats.water_target) * 100)) : 0}
              color={stats?.water_glasses >= (stats?.water_target || 10) * 0.7 ? 'emerald' : 'orange'} />
          </div>

          {/* Boost CTA */}
          {(() => {
            if (activeTab !== 'today' || !hasToday) return null;
            const hour = today?.hour_local ?? new Date().getHours();
            const proteinPct = today?.protein_pct ?? 0;
            const caloriesPct = today?.calories_pct ?? 0;
            if (hour < 15 || (proteinPct >= 50 && caloriesPct >= 50)) return null;
            const worstLabel = proteinPct < caloriesPct
              ? t(`tu es à ${proteinPct}% protéines`, `${proteinPct}% protein`)
              : t(`tu es à ${caloriesPct}% calories`, `${caloriesPct}% calories`);
            return (
              <button
                onClick={() => { onClose(); navigate('/meals'); }}
                className="w-full mb-4 group flex items-center justify-between gap-3 rounded-lg border border-orange-500/40 bg-gradient-to-r from-orange-500/15 via-amber-500/10 to-orange-500/15 hover:from-orange-500/25 px-3 py-2.5 text-left active:scale-[0.98] transition-all"
                data-testid="morning-debrief-boost-cta"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg shrink-0" aria-hidden="true">🚀</span>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-amber-300 leading-tight">
                      {t('Boost session — on rattrape ?', "Boost session — let's catch up!")}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      {worstLabel} · {t('scanne un repas maintenant', 'scan a meal now')}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-amber-400 group-hover:translate-x-0.5 transition-transform shrink-0" />
              </button>
            );
          })()}

          {/* Active alert */}
          {alert_mode && active_alert && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-500/15 border border-red-500/30 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-300 uppercase tracking-wide mb-0.5">
                  {t('Alerte de dérive détectée', 'Drift alert detected')} — {active_alert.consecutive_days}j
                </p>
                <p className="text-xs text-red-200/80">{active_alert.description}</p>
              </div>
            </div>
          )}

          {/* Suggestions */}
          {suggestions && suggestions.length > 0 && (
            <div className="space-y-2 mb-4" data-testid="morning-suggestions">
              <div className="flex items-center gap-1.5">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-heading uppercase tracking-wide text-amber-300">
                  {t("Plan d'action pour aujourd'hui", "Today's action plan")}
                </span>
              </div>
              {suggestions.map((s, i) => {
                const text = (typeof s === 'string') ? s : (s?.text || s?.suggestion || s?.label || s?.message || '');
                if (!text) return null;
                // Iter. 406.fr.34/.35/.36 — Color-tag per Yuri spec.
                // Backend exposes today.briefing_axes = [{kind, priority}, ...]
                // aligned with `suggestions` (same index). Map BY KIND first
                // (most precise) then fall back to priority.
                const axis = (today?.briefing_axes || [])[i];
                const priority = axis?.priority;
                const kind = axis?.kind || '';
                let style, icon, badge;
                if (kind === 'medical_shield') {
                  style = 'bg-red-500/15 border-red-500/50 shadow-[0_0_12px_rgba(239,68,68,0.30)]';
                  icon = '🛡️';
                  badge = { label: t('Bouclier médical', 'Medical shield'), color: 'bg-red-500/30 text-red-200' };
                } else if (kind === 'water_critical' || kind === 'water_low') {
                  style = 'bg-red-500/15 border-red-500/50 shadow-[0_0_12px_rgba(239,68,68,0.25)]';
                  icon = '🚨';
                  badge = { label: t('Critique', 'Critical'), color: 'bg-red-500/30 text-red-200' };
                } else if (kind && kind.startsWith('nutrition_')) {
                  // P2 obligatoire — coeur de YUMIX. Orange chaleureux, jamais fade.
                  style = 'bg-amber-500/15 border-amber-500/50 shadow-[0_0_12px_rgba(251,191,36,0.28)]';
                  icon = '🥗';
                  badge = { label: t('Nutrition', 'Nutrition'), color: 'bg-amber-500/30 text-amber-100' };
                } else if (kind === 'activity_move') {
                  // Iter. 406.fr.38 — pilier Activité du Score de Discipline
                  style = 'bg-violet-500/15 border-violet-500/50 shadow-[0_0_12px_rgba(139,92,246,0.28)]';
                  icon = '💪';
                  badge = { label: t('Activité', 'Activity'), color: 'bg-violet-500/30 text-violet-100' };
                } else if (kind === 'hydration_water') {
                  // Iter. 406.fr.38 — pilier Hydratation
                  style = 'bg-cyan-500/15 border-cyan-500/50 shadow-[0_0_12px_rgba(34,211,238,0.28)]';
                  icon = '💧';
                  badge = { label: t('Hydratation', 'Hydration'), color: 'bg-cyan-500/30 text-cyan-100' };
                } else if (kind === 'sleep_routine') {
                  // Iter. 406.fr.38 — pilier Sommeil
                  style = 'bg-indigo-500/15 border-indigo-500/50 shadow-[0_0_12px_rgba(99,102,241,0.28)]';
                  icon = '🌙';
                  badge = { label: t('Sommeil', 'Sleep'), color: 'bg-indigo-500/30 text-indigo-100' };
                } else if (kind === 'mindset_reflect') {
                  // Iter. 406.fr.38 — pilier Mindset
                  style = 'bg-fuchsia-500/15 border-fuchsia-500/50 shadow-[0_0_12px_rgba(217,70,239,0.28)]';
                  icon = '🧠';
                  badge = { label: t('Mindset', 'Mindset'), color: 'bg-fuchsia-500/30 text-fuchsia-100' };
                } else if (kind && kind.startsWith('celebrate_')) {
                  style = 'bg-emerald-500/15 border-emerald-500/50 shadow-[0_0_12px_rgba(16,185,129,0.30)]';
                  icon = '🏆';
                  badge = { label: t('Bravo', 'Bravo'), color: 'bg-emerald-500/30 text-emerald-200' };
                } else if (kind === 'workout_focus') {
                  style = 'bg-blue-500/12 border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.22)]';
                  icon = '🎯';
                  badge = { label: t('Focus', 'Focus'), color: 'bg-blue-500/30 text-blue-200' };
                } else if (kind === 'rest_day') {
                  style = 'bg-cyan-500/12 border-cyan-500/40';
                  icon = '🌿';
                  badge = { label: t('Repos', 'Rest'), color: 'bg-cyan-500/30 text-cyan-200' };
                } else if (priority === 1) {
                  style = 'bg-red-500/15 border-red-500/50';
                  icon = '🚨';
                  badge = { label: t('Critique', 'Critical'), color: 'bg-red-500/30 text-red-200' };
                } else {
                  style = 'bg-secondary/60 border-border/50';
                  icon = '💡';
                  badge = null;
                }
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${style} transition-all`}
                    data-testid={`morning-suggestion-${i}`}
                    data-priority={priority || 4}
                    data-kind={kind || 'generic'}
                  >
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full bg-black/40 text-base flex items-center justify-center mt-0.5 leading-none"
                      aria-hidden="true"
                    >
                      {icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      {badge && (
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest mb-1 ${badge.color}`}
                          data-testid={`morning-suggestion-${i}-badge`}
                        >
                          {badge.label}
                        </span>
                      )}
                      <p className="text-xs text-foreground/90 leading-relaxed">{text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Gaps */}
          {hasGaps && (
            <div className="space-y-1.5 mb-4">
              {gaps.map((g, i) => (
                <div key={i} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg ${
                  g.severity === 'critical' ? 'bg-red-500/10 text-red-300' :
                  g.severity === 'warning' ? 'bg-orange-500/10 text-orange-300' : 'bg-blue-500/10 text-blue-300'
                }`}>
                  {g.severity === 'critical' || g.severity === 'warning'
                    ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    : <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span>{g.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Iter. 406.fr.37 — Medical Shield footnote discrète en bas
            (avant le footer sticky). Une seule ligne, gris fade, jamais
            de gros bloc rouge. */}
        {today?.medical_footnote && (
          <div
            className="px-5 pb-3 text-[11px] text-muted-foreground/80 leading-snug border-t border-border/30 pt-3"
            data-testid="morning-medical-footnote"
          >
            {today.medical_footnote}
          </div>
        )}

        {/* Footer (sticky) */}
        <div className="px-5 py-3 border-t border-border/40 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
            onClick={() => { onClose(); navigate('/monthly-report'); }}
            data-testid="morning-debrief-report-btn"
          >
            <BarChart3 className="w-3.5 h-3.5 mr-2" />
            {t('Voir mon rapport complet', 'View full report')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="text-xs text-muted-foreground"
            data-testid="morning-debrief-modal-footer-close"
          >
            {t('Fermer', 'Close')}
          </Button>
        </div>
        {/* eslint-disable-next-line no-unused-expressions */}
        {fr}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
// PillarDots — moved to `components/PillarDots.js` in Iter. 406.fr.53.
// The extracted component now dispatches `yumix:pillar-state` and
// `yumix:perfect-day` window events consumed by InAppNotificationManager.
// ════════════════════════════════════════════════════════════════════════

const StatPill = ({ icon, label, value, pct, color }) => {
  const colors = { emerald: 'text-emerald-400', orange: 'text-orange-400', red: 'text-red-400' };
  const bgColors = { emerald: 'bg-emerald-500/15', orange: 'bg-orange-500/15', red: 'bg-red-500/15' };
  return (
    <div className={`rounded-lg p-2 text-center ${bgColors[color] || bgColors.orange}`}>
      <div className={`flex items-center justify-center gap-1 ${colors[color] || colors.orange} mb-0.5`}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg font-heading font-black ${colors[color]}`}>{value}</p>
      <div className="w-full bg-black/20 rounded-full h-1 mt-1">
        <div
          className={`h-1 rounded-full transition-all ${color === 'emerald' ? 'bg-emerald-400' : color === 'red' ? 'bg-red-400' : 'bg-orange-400'}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
};

export default MorningDebrief;
